import { useMoneyFormatter } from "@/lib/money-format";
import { useActiveTeam, useTeamPermission } from "@/lib/team-context";
import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  ArrowDownLeft,
  ArrowLeft,
  ArrowUpRight,
  Download,
  Loader2,
  Pencil,
  Printer,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useDiscardSalesInvoice,
  useDownloadInvoicePdf,
  useEmitSalesInvoice,
  useRefreshSalesInvoiceStatus,
  useSalesInvoice,
} from "@/lib/api/sales-invoices.queries";
import { translateInvoiceError } from "@/components/sales-invoices/error-translations";
import { InvoiceForm } from "@/components/sales-invoices/InvoiceForm";
import { DiscardDraftDialog } from "@/components/shared/DiscardDraftDialog";
import { STATUS_BADGE_VARIANT } from "@/components/sales-invoices/format";
import { InvoiceEmailResend, formatDeliveryTime } from "@/components/sales-invoices/InvoiceEmailResend";
import { InvoiceArtifactsPanel } from "@/components/sales-invoices/InvoiceArtifactsPanel";
import { InvoiceCufeCard } from "@/components/sales-invoices/InvoiceCufeCard";
import { InvoiceStatusTimeline } from "@/components/sales-invoices/InvoiceStatusTimeline";
import { InvoiceRadianEventsPanel } from "@/components/radian/InvoiceRadianEventsPanel";
import { DocumentJournalPanel } from "@/components/journal/DocumentJournalPanel";
import { DocumentAttachments } from "@/components/documents/DocumentAttachments";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import {
  useCompanyDianReadiness,
  useDianReadinessReason,
} from "@/lib/dian/use-company-dian-readiness";
import { useActiveCompany } from "@/lib/company-context";
import { showEmitErrorToast } from "@/lib/dian/emit-error-toast";

export const Route = createFileRoute("/companies/$companyId/facturas/$invoiceId/")({
  component: InvoiceDetailPage,
});

function InvoiceDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <InvoiceDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function InvoiceDetailContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, invoiceId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const role = useActiveTeam().activeTeam?.role;
  const cashier = role === "cashier";
  const canWrite = useTeamPermission("sales_invoices.write");
  const invoiceQuery = useSalesInvoice(companyId, invoiceId);
  const refresh = useRefreshSalesInvoiceStatus(companyId);
  const emit = useEmitSalesInvoice(companyId);
  const downloadPdf = useDownloadInvoicePdf(companyId);
  const discard = useDiscardSalesInvoice(companyId);
  const [editing, setEditing] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const readiness = useCompanyDianReadiness(companyId, "factura_venta");
  const readinessReason = useDianReadinessReason(readiness, t);

  if (invoiceQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (invoiceQuery.isError || !invoiceQuery.data) {
    return (
      <ErrorState
        title={t("invoices.detail.error_title")}
        description={t("invoices.detail.error_body")}
        onRetry={() => void invoiceQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const invoice = invoiceQuery.data;
  const canRefresh = !!invoice.processId;
  const canEmit = canWrite && (invoice.status === "draft" || invoice.status === "failed");
  // A draft, or an invoice DIAN confirmed as rejected, can be reopened and corrected (PF-463).
  const canEdit = canWrite && invoice.editable;
  const canDiscard = canWrite && invoice.status === "draft" && invoice.documentNumber === 0;
  // The borrador PDF preview only exists pre-emission (the BFF 409s
  // otherwise); emitted documents get their PDF from the DIAN artifacts.
  const canPreview = invoice.status === "draft" || invoice.status === "failed";
  const artifactsEnabled = invoice.status === "emitted" || invoice.status === "failed";

  async function onRefresh() {
    try {
      await refresh.mutateAsync({ invoiceId });
      toast.success(t("invoices.detail.refresh_done"));
    } catch (err) {
      toast.error(translateInvoiceError(err, t));
    }
  }

  async function onDiscard() {
    try {
      await discard.mutateAsync({ invoiceId, expectedRevision: invoice.revision });
      toast.success(t("invoices.detail.discarded"));
      setDiscardOpen(false);
      void navigate({ to: "/companies/$companyId/facturas", params: { companyId } });
    } catch (err) {
      toast.error(translateInvoiceError(err, t));
      setDiscardOpen(false);
    }
  }

  async function onEmit() {
    try {
      await emit.mutateAsync({ invoiceId });
      toast.success(t("invoices.detail.emit_queued"));
    } catch (err) {
      showEmitErrorToast(err, translateInvoiceError(err, t), t, () =>
        void navigate({
          to: "/companies/$companyId/resoluciones",
          params: { companyId },
        }),
      );
    }
  }

  // Persisted representación gráfica (the `pdf` DIAN artifact): generated
  // server-side on the first request, then always the same stored bytes.
  // Same objectURL + synthetic <a download> pattern as the preview page.
  async function onDownloadPdf() {
    try {
      const blob = await downloadPdf.mutateAsync({ invoiceId });
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `${invoice.documentId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      toast.error(translateInvoiceError(err, t));
    }
  }


  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="factura_venta" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/facturas"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("invoices.detail.back")}
          </Link>
          <div className="mt-1 flex items-center gap-2">
            <h1 className="font-mono text-2xl font-semibold tracking-tight">
              {invoice.documentId}
            </h1>
            <Badge variant={STATUS_BADGE_VARIANT[invoice.status]} className="text-[10px] uppercase">
              {t(`invoices.status.${invoice.status}`)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {invoice.customerLegalName} - {formatCop(invoice.payableAmount)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canRefresh ? (
            <Button variant="outline" onClick={() => void onRefresh()} disabled={refresh.isPending}>
              <RefreshCw className={refresh.isPending ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
              {t("invoices.detail.refresh_status")}
            </Button>
          ) : null}
          {canEdit && !editing ? (
            <Button variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              {t("invoices.detail.edit")}
            </Button>
          ) : null}
          {canDiscard && !editing ? (
            <Button variant="outline" onClick={() => setDiscardOpen(true)} disabled={discard.isPending}>
              <Trash2 className="h-4 w-4" />
              {t("invoices.detail.discard")}
            </Button>
          ) : null}
          {canEmit && !editing ? (
            <Button
              onClick={() => void onEmit()}
              disabled={emit.isPending || !readiness.ready}
              title={!readiness.ready ? readinessReason : undefined}
            >
              <Send className="h-4 w-4" />
              {t("invoices.detail.emit_now")}
            </Button>
          ) : null}
          {invoice.status === "emitted" ? (
            <>
              <InvoiceEmailResend companyId={companyId} invoice={invoice} role={role} canWrite={canWrite} />
              <Button
                variant="outline"
                onClick={() => void onDownloadPdf()}
                disabled={downloadPdf.isPending}
              >
                {downloadPdf.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {t("invoices.detail.download_pdf")}
              </Button>
              {!cashier ? <><Button asChild variant="outline">
                <Link
                  to="/companies/$companyId/notas-credito/new"
                  params={{ companyId }}
                  search={{ fromInvoice: invoiceId }}
                >
                  <ArrowDownLeft className="h-4 w-4" />
                  {t("notes.invoice_cta.create_credit")}
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link
                  to="/companies/$companyId/notas-debito/new"
                  params={{ companyId }}
                  search={{ fromInvoice: invoiceId }}
                >
                  <ArrowUpRight className="h-4 w-4" />
                  {t("notes.invoice_cta.create_debit")}
                </Link>
              </Button></> : null}
            </>
          ) : null}
          {canPreview && !editing ? (
            <Button asChild variant="outline">
              <Link
                to="/companies/$companyId/facturas/$invoiceId/preview"
                params={{ companyId, invoiceId }}
              >
                <Printer className="h-4 w-4" />
                {t("invoices.detail.preview")}
              </Link>
            </Button>
          ) : null}
        </div>
      </div>

      {editing ? (
        <InvoiceForm
          companyId={companyId}
          initialInvoice={invoice}
          onSaved={() => setEditing(false)}
          onCancel={() => setEditing(false)}
        />
      ) : null}

      <Tabs defaultValue="overview" className={editing ? "hidden" : "w-full"}>
        <TabsList>
          <TabsTrigger value="overview">{t("invoices.detail.tab.overview")}</TabsTrigger>
          <TabsTrigger value="lines">{t("invoices.detail.tab.lines")}</TabsTrigger>
          <TabsTrigger value="dian">{t("invoices.detail.tab.dian")}</TabsTrigger>
          {!cashier ? <TabsTrigger value="accounting">{t("invoices.detail.tab.accounting")}</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="overview" className="mt-4 grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("invoices.detail.summary.title")}</CardTitle>
              <CardDescription>{t("invoices.detail.summary.subtitle")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label={t("invoices.detail.field.customer")} value={invoice.customerLegalName ?? "-"} />
              <Row label={t("invoices.detail.field.identification")} value={invoice.customerIdentificationNumber ?? "-"} />
              <Row label={t("invoices.detail.field.issue")} value={invoice.issueDateTime} />
              <Row label={t("invoices.detail.field.due")} value={invoice.paymentDueDate ?? "-"} />
              <Row label={t("invoices.detail.field.payment_means")} value={t(`paymentMeans.${invoice.paymentMeansCode}`, { defaultValue: invoice.paymentMeansCode })} />
              <Row label={t("invoices.detail.field.invoice_type")} value={t(`invoices.invoiceType.${invoice.invoiceTypeCode}`)} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("invoices.totals.title")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              <Row label={t("invoices.totals.list_price")} value={formatCop(invoice.grossLineAmount)} />
              {invoice.lineDiscountAmount > 0 ? (
                <Row label={t("invoices.totals.line_discount")} value={`- ${formatCop(invoice.lineDiscountAmount)}`} />
              ) : null}
              <Row label={t("invoices.totals.net_subtotal")} value={formatCop(invoice.lineExtensionAmount)} />
              {invoice.allowanceTotalAmount > 0 ? (
                <Row label={t("invoices.totals.allowance")} value={`- ${formatCop(invoice.allowanceTotalAmount)}`} />
              ) : null}
              {invoice.chargeTotalAmount > 0 ? (
                <Row label={t("invoices.totals.charge")} value={`+ ${formatCop(invoice.chargeTotalAmount)}`} />
              ) : null}
              <Row label={t("invoices.totals.tax_exclusive")} value={formatCop(invoice.taxExclusiveAmount)} bold />
              <Row label={t("invoices.totals.iva")} value={formatCop(invoice.ivaAmount)} />
              {invoice.icAmount > 0 ? <Row label={t("invoices.totals.ic")} value={formatCop(invoice.icAmount)} /> : null}
              {invoice.icaAmount > 0 ? <Row label={t("invoices.totals.ica")} value={formatCop(invoice.icaAmount)} /> : null}
              {invoice.incAmount > 0 ? <Row label={t("invoices.totals.inc")} value={formatCop(invoice.incAmount)} /> : null}
              {invoice.prepaidAmount > 0 ? (
                <Row label={t("invoices.totals.prepaid")} value={`- ${formatCop(invoice.prepaidAmount)}`} />
              ) : null}
              <div className="border-t pt-2">
                <Row label={t("invoices.totals.payable")} value={formatCop(invoice.payableAmount)} bold />
              </div>
            </CardContent>
          </Card>
          {invoice.status === "emitted" ? (
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">{t("invoices.detail.email.title")}</CardTitle>
                <CardDescription>{t("invoices.detail.email.subtitle")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <Row
                  label={t("invoices.detail.email.status")}
                  value={t(
                    `invoices.detail.email.status_${invoice.emailDelivery?.status ?? "not_sent"}`,
                  )}
                  bold={invoice.emailDelivery?.status === "sent"}
                />
                {invoice.emailDelivery ? (
                  <>
                    <Row
                      label={t("invoices.detail.email.recipient")}
                      value={invoice.emailDelivery.recipientEmail}
                    />
                    <Row
                      label={t("invoices.detail.email.attempts")}
                      value={String(invoice.emailDelivery.attemptCount)}
                    />
                    {invoice.emailDelivery.additionalRecipientEmail ? <Row label={t("invoices.detail.email.additional")} value={invoice.emailDelivery.additionalRecipientEmail} /> : null}
                    {invoice.emailDelivery.attemptedAt ? <Row label={t("invoices.detail.email.attempted_at")} value={formatDeliveryTime(invoice.emailDelivery.attemptedAt)} /> : null}
                    {invoice.emailDelivery.sentAt ? (
                      <Row
                        label={t("invoices.detail.email.sent_at")}
                        value={formatDeliveryTime(invoice.emailDelivery.sentAt)}
                      />
                    ) : null}
                    {invoice.emailDelivery.lastError ? (
                      <p className="text-sm text-destructive">
                        {invoice.emailDelivery.lastError}
                      </p>
                    ) : null}
                  </>
                ) : null}
                {invoice.emailDeliveryHistory?.length ? <details className="pt-3"><summary>{t("invoices.detail.email.history")}</summary>
                  <ul className="mt-2 space-y-2">{invoice.emailDeliveryHistory.map(attempt => <li key={attempt.attemptCount} className="rounded border p-2">
                    <p>#{attempt.attemptCount} · {t(`invoices.detail.email.status_${attempt.status}`)} · {attempt.recipientEmail}{attempt.additionalRecipientEmail ? ` / ${attempt.additionalRecipientEmail}` : ""}</p>
                    {attempt.attemptedAt ? <p>{formatDeliveryTime(attempt.attemptedAt)}</p> : null}
                    {attempt.lastError ? <p className="text-destructive">{attempt.lastError}</p> : null}
                  </li>)}</ul></details> : null}
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>

        <TabsContent value="lines" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>{t("invoices.line.description")}</TableHead>
                    <TableHead className="text-right">{t("invoices.line.quantity")}</TableHead>
                    <TableHead className="text-right">{t("invoices.line.unit_price")}</TableHead>
                    <TableHead className="text-right">{t("invoices.line.discount")}</TableHead>
                    <TableHead className="text-right">{t("invoices.line.subtotal")}</TableHead>
                    <TableHead className="text-right">{t("invoices.line.tax")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoice.lines.map((line) => (
                    <TableRow key={line.lineId}>
                      <TableCell className="text-xs text-muted-foreground">{line.lineId}</TableCell>
                      <TableCell>
                        <p className="text-sm font-medium">{line.description}</p>
                        {line.sku ? (
                          <p className="font-mono text-[10px] text-muted-foreground">SKU {line.sku}</p>
                        ) : null}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {line.quantity} {line.unitCode}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(line.unitPrice)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {line.allowanceAmount > 0 ? (
                          <>
                            <span>{line.discountPercent}%</span>
                            <span className="block text-xs text-muted-foreground">
                              - {formatCop(line.allowanceAmount)}
                            </span>
                          </>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(line.lineExtensionAmount)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCop(
                          line.ivaAmount + line.icAmount + line.icaAmount + line.incAmount,
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dian" className="mt-4 grid gap-4 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("invoices.detail.timeline_title")}</CardTitle>
              <CardDescription>{t("invoices.detail.timeline_subtitle")}</CardDescription>
            </CardHeader>
            <CardContent>
              <InvoiceStatusTimeline invoice={invoice} />
            </CardContent>
          </Card>
          <div className="space-y-4">
            <InvoiceCufeCard invoice={invoice} />
            <InvoiceArtifactsPanel
              companyId={companyId}
              invoiceId={invoiceId}
              enabled={artifactsEnabled}
            />
            {/* Eventos RADIAN (PF-339): buyer-registered events + the 034
                tácita — only meaningful once the invoice holds a CUFE. */}
            {!cashier && invoice.cufe ? (
              <InvoiceRadianEventsPanel
                companyId={companyId}
                invoiceId={invoiceId}
              />
            ) : null}
          </div>
        </TabsContent>

        {!cashier ? <TabsContent value="accounting" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t("journal.document.title")}</CardTitle>
              <CardDescription>{t("journal.document.subtitle")}</CardDescription>
            </CardHeader>
            <CardContent>
              <DocumentJournalPanel
                companyId={companyId}
                sourceTable="sales_invoices"
                documentId={invoiceId}
              />
            </CardContent>
          </Card>
        </TabsContent> : null}
      </Tabs>

      {/* Soportes vinculados a la factura (orden de compra, remisión…) — PF-348 */}
      {!cashier && !editing ? <DocumentAttachments
        companyId={companyId}
        entityType="sales_invoice"
        entityId={invoiceId}
      /> : null}

      <DiscardDraftDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={t("invoices.detail.discard_title", { documentId: invoice.documentId })}
        description={t("invoices.detail.discard_body")}
        pending={discard.isPending}
        onConfirm={() => void onDiscard()}
      />
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}>
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
